/** Albarán de recogida.
 *
 *  El único documento del cajón que NO es de un clic: de sus 19 campos, once
 *  salen del sistema y ocho los teclea una persona, porque no existen en ningún
 *  sitio. De ahí el formulario.
 *
 *  Tres cosas que no se pueden hacer mal:
 *
 *  1. El incoterm va en DOS campos —código y puerto— y se juntan al generar. El
 *     puerto no se deduce del destino (acierta en 8 de 15, lo justo para engañar).
 *  2. Un `sin_bultos_peso` se ve: bultos y peso llegan vacíos, no a cero, y un
 *     albarán con el peso en blanco sin decir por qué es lo que se firma sin mirar.
 *  3. Contenedor y tipo de envío llegarán casi siempre vacíos y no es un fallo. */
import { useTranslation } from 'react-i18next'

import { Loader } from '@/components/common/Loader'
import type { AlbaranRecogidaEstado } from '@/hooks/useAlbaranRecogida'
import {
  avisosVisibles,
  componerIncoterm,
  totalesLineas,
  type AlbaranForm,
} from '@/utils/albaranRecogida'

import { BloqueAvisos, PieGenerar, Seccion, fmtEnt, fmtKg } from './piezas'

type ClaveTexto = Exclude<keyof AlbaranForm, 'lineas'>

export function PanelAlbaranRecogida({
  dark, albaran,
}: {
  dark: boolean
  albaran: AlbaranRecogidaEstado
}) {
  const { t } = useTranslation()
  const { form, datos, generar } = albaran

  const mutedTxt = dark ? 'text-slate-400' : 'text-slate-500'
  const label = `text-[9px] font-bold uppercase tracking-wider ${dark ? 'text-slate-500' : 'text-slate-400'}`
  const input = `w-full mt-1 px-2 py-1.5 rounded-md text-[11px] border outline-none transition-colors ${
    dark
      ? 'bg-white/[0.04] border-white/10 text-slate-200 placeholder:text-slate-600 focus:border-sky-500/60'
      : 'bg-white border-slate-200 text-slate-800 placeholder:text-slate-300 focus:border-sky-400'
  }`

  // La precarga tarda unos segundos en frío. Mientras tanto, el loader común.
  if (albaran.cargando || !form) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader label={t('salesOrders.documentos.albaranRecogida.cargando')} />
      </div>
    )
  }

  // El botón se queda en pendiente hasta que el servidor contesta; el formulario
  // no se toca, así que un rechazo lo devuelve tal cual.
  if (generar.isPending) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader label={t('salesOrders.documentos.generando')} />
      </div>
    )
  }

  const campo = (clave: ClaveTexto, opts: { tipo?: 'date'; ph?: string; ayuda?: string } = {}) => (
    <div key={clave}>
      <label className={label} htmlFor={`alb-${clave}`}>
        {t(`salesOrders.documentos.albaranRecogida.${clave}`)}
      </label>
      <input
        id={`alb-${clave}`}
        type={opts.tipo ?? 'text'}
        value={form[clave]}
        onChange={e => albaran.poner({ [clave]: e.target.value })}
        placeholder={opts.ph ?? t('salesOrders.documentos.form.vacio')}
        className={input}
      />
      {opts.ayuda && <p className={`text-[9px] mt-0.5 ${mutedTxt}`}>{opts.ayuda}</p>}
    </div>
  )

  const avisos = avisosVisibles(datos)
  const totales = totalesLineas(form.lineas)
  const incotermFinal = componerIncoterm(form.incoterm, form.incotermPuerto)
  const celda = `w-full px-1.5 py-1 rounded text-[11px] border outline-none ${
    dark
      ? 'bg-white/[0.04] border-white/10 text-slate-200 focus:border-sky-500/60'
      : 'bg-white border-slate-200 text-slate-800 focus:border-sky-400'
  }`

  return (
    <>
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
        <p className={`text-[11px] ${mutedTxt}`}>{t('salesOrders.documentos.albaranRecogida.nota')}</p>

        {albaran.errorDatos && (
          <p className={`text-[11px] ${dark ? 'text-amber-300' : 'text-amber-700'}`}>
            {t('salesOrders.documentos.albaranRecogida.errorDatos')}
          </p>
        )}

        <BloqueAvisos dark={dark} bloqueantes={[]} informativos={avisos} />

        <Seccion
          titulo={t('salesOrders.documentos.albaranRecogida.secRecogida')}
          nota={t('salesOrders.documentos.albaranRecogida.secRecogidaNota')}
          dark={dark}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className={label} htmlFor="alb-puntoRecogida">
                {t('salesOrders.documentos.albaranRecogida.puntoRecogida')}
              </label>
              <textarea
                id="alb-puntoRecogida"
                rows={4}
                value={form.puntoRecogida}
                onChange={e => albaran.poner({ puntoRecogida: e.target.value })}
                placeholder={t('salesOrders.documentos.form.vacio')}
                className={input}
              />
            </div>
            {campo('telefono')}
            {campo('correo')}
          </div>
        </Seccion>

        <Seccion titulo={t('salesOrders.documentos.albaranRecogida.secCita')} dark={dark}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {campo('fechaRecogida', { tipo: 'date' })}
            {campo('horario')}
            {campo('personaEncargada')}
          </div>
        </Seccion>

        <Seccion titulo={t('salesOrders.documentos.albaranRecogida.secEnvio')} dark={dark}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {campo('pedido')}
            {campo('transportista')}
            {/* Llegan casi siempre vacíos: es lo normal, no un error. */}
            {campo('tipoEnvio', { ayuda: t('salesOrders.documentos.albaranRecogida.vacioNormal') })}
            {campo('contenedor', { ayuda: t('salesOrders.documentos.albaranRecogida.vacioNormal') })}
            {campo('tipoPalet')}
            {campo('tipoCarga')}
          </div>

          {/* Código y puerto, separados: nadie borra el código al escribir el puerto. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
            {campo('incoterm')}
            {campo('incotermPuerto', { ph: t('salesOrders.documentos.albaranRecogida.incotermPuertoPh') })}
            <div className="flex items-end pb-2">
              <p className={`text-[11px] font-mono ${mutedTxt}`}>
                {t('salesOrders.documentos.albaranRecogida.incotermResultado', {
                  valor: incotermFinal || '—',
                })}
              </p>
            </div>
          </div>
          {datos?.incotermAmbiguo && (
            <p className={`text-[10px] mt-1 ${dark ? 'text-amber-300' : 'text-amber-700'}`}>
              {t('salesOrders.documentos.albaranRecogida.incotermAmbiguo')}
            </p>
          )}
        </Seccion>

        <Seccion titulo={t('salesOrders.documentos.albaranRecogida.secPartes')} dark={dark}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {campo('destinatario')}
            {campo('destino')}
          </div>
        </Seccion>

        <Seccion
          titulo={t('salesOrders.documentos.albaranRecogida.secLineas')}
          nota={t('salesOrders.documentos.albaranRecogida.secLineasNota')}
          dark={dark}
          accion={
            <button
              type="button"
              onClick={albaran.anadirLinea}
              className={`px-2.5 py-1 rounded-md text-[10px] font-semibold cursor-pointer ${
                dark ? 'text-sky-300 hover:bg-white/6' : 'text-sky-700 hover:bg-slate-100'}`}
            >
              {t('salesOrders.documentos.albaranRecogida.anadirLinea')}
            </button>
          }
        >
          <div className="space-y-2">
            <div className={`hidden sm:grid grid-cols-[1fr_5rem_2fr_6rem_1.5rem] gap-2 ${label}`}>
              <span>{t('salesOrders.documentos.albaranRecogida.palets')}</span>
              <span>{t('salesOrders.documentos.albaranRecogida.bultos')}</span>
              <span>{t('salesOrders.documentos.albaranRecogida.contenido')}</span>
              <span>{t('salesOrders.documentos.albaranRecogida.peso')}</span>
              <span />
            </div>
            {form.lineas.map(l => (
              <div key={l.id} className="grid grid-cols-2 sm:grid-cols-[1fr_5rem_2fr_6rem_1.5rem] gap-2 items-center">
                <input
                  aria-label={t('salesOrders.documentos.albaranRecogida.palets')}
                  value={l.palets}
                  placeholder={t('salesOrders.documentos.albaranRecogida.paletsPh')}
                  onChange={e => albaran.ponerLinea(l.id, { palets: e.target.value })}
                  className={celda}
                />
                <input
                  aria-label={t('salesOrders.documentos.albaranRecogida.bultos')}
                  inputMode="decimal"
                  value={l.bultos}
                  placeholder="—"
                  onChange={e => albaran.ponerLinea(l.id, { bultos: e.target.value })}
                  className={`${celda} text-right tabular-nums`}
                />
                <input
                  aria-label={t('salesOrders.documentos.albaranRecogida.contenido')}
                  value={l.contenido}
                  onChange={e => albaran.ponerLinea(l.id, { contenido: e.target.value })}
                  className={celda}
                />
                <input
                  aria-label={t('salesOrders.documentos.albaranRecogida.peso')}
                  inputMode="decimal"
                  value={l.peso}
                  placeholder="—"
                  onChange={e => albaran.ponerLinea(l.id, { peso: e.target.value })}
                  className={`${celda} text-right tabular-nums`}
                />
                <button
                  type="button"
                  onClick={() => albaran.quitarLinea(l.id)}
                  disabled={form.lineas.length <= 1}
                  aria-label={t('salesOrders.documentos.albaranRecogida.quitarLinea')}
                  title={t('salesOrders.documentos.albaranRecogida.quitarLinea')}
                  className={`text-[14px] leading-none ${
                    form.lineas.length <= 1
                      ? 'opacity-30 cursor-not-allowed'
                      : `cursor-pointer ${dark ? 'text-slate-500 hover:text-red-400' : 'text-slate-400 hover:text-red-600'}`}`}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          <p className={`text-[10px] mt-2 ${mutedTxt}`}>
            {t('salesOrders.documentos.albaranRecogida.totalLineas', {
              bultos: fmtEnt(totales.bultos) ?? '—',
              peso: fmtKg(totales.peso) ?? '—',
            })}
          </p>
        </Seccion>

        <Seccion titulo={t('salesOrders.documentos.albaranRecogida.observaciones')} dark={dark}>
          <textarea
            id="alb-observaciones"
            aria-label={t('salesOrders.documentos.albaranRecogida.observaciones')}
            rows={3}
            value={form.observaciones}
            onChange={e => albaran.poner({ observaciones: e.target.value })}
            className={input}
          />
        </Seccion>
      </div>

      <PieGenerar
        dark={dark}
        errorGen={albaran.errorGen}
        bloqueantes={0}
        listas={t('salesOrders.documentos.albaranRecogida.listo')}
        numCorregidos={0}
        numExcluidas={0}
        onRestablecer={() => {}}
        puedeGenerar={!generar.isPending}
        generar={t('salesOrders.documentos.albaranRecogida.generar')}
        onGenerar={() => generar.mutate()}
      />
    </>
  )
}
